// The Avenue School/the-avenue-school/Client/src/components/Curriculum.js
import React from 'react';
import { Container, Row, Col, Accordion } from 'react-bootstrap';
import PageHeader from './PageHeader';
import '../styles/Curriculum.css';

/**
 * Curriculum page
 * - PageHeader (top)
 * - Figma content block (intro card + two framed images + two-column section)
 * - FAQs (styled like the provided Figma; uses <details>/<summary> for a11y)
 *
 * Props (minimal, safe defaults):
 * - heroEyebrow?: string
 * - heroTitle?: string
 * - images?: { left: string, right: string }
 */

const Curriculum = ({
  heroEyebrow = 'THE AVENUE SCHOOL',
  heroTitle = 'Curriculum',
  images = {
    left: '/media/gallery-1.jpg',
    right: '/media/gallery-2.jpg',
  },
}) => {
  return (
    <main className="curriculum" aria-labelledby="curriculum-heading">
      {/* Page Header */}
      <PageHeader eyebrow={heroEyebrow} title={heroTitle} image="/media/gallery-4.jpg"
        height="200px"/>
      {/* Hidden page landmark (for skip links / a11y) */}
      <h1 id="curriculum-heading" className="sr-only">Curriculum</h1>

      {/* ===== Figma block: intro card + two images ===== */}
      <div className="curriculum__wrap">
        <Container className="curriculum__introContainer">
          <div className="curriculum__introCard">
            <div className="curriculum__introHeader">
              <p className="curriculum__eyebrow">{heroEyebrow}</p>
              <h2 className="curriculum__title">Curriculum</h2>
              <p className="curriculum__lead">
                At The Avenue School, our curriculum is designed to meet students where they are. We offer the Victorian Pathways Certificate (VPC), a flexible and inclusive senior secondary qualification tailored for students aged 15–20. Our approach blends academic learning with real-world skills, personal development, and wellbeing support.
                <br /><br />
                Students follow Individual Learning Plans that reflect their goals, interests, and strengths—whether that’s trades, nursing, or creative pathways. With small class sizes and trauma-informed teaching, we create a calm, supportive environment where students can build confidence, re-engage with learning, and prepare for further education, training, or employment.
              </p>
            </div>

            <Row className="g-4 curriculum__imageRow">
              <Col md={6}>
                <figure className="curriculum__imageFrame">
                  <img src={images.left} alt="" loading="lazy" />
                </figure>
              </Col>
              <Col md={6}>
                <figure className="curriculum__imageFrame curriculum__imageFrame--padB">
                  <img src={images.right} alt="" loading="lazy" />
                </figure>
              </Col>
            </Row>
          </div>
        </Container>

        {/* ===== Figma block: two-column section ===== */}
        <Container className="curriculum__twoColContainer">
          <Row className="g-5 align-items-start">
            <Col md={6}>
              <section className="curriculum__col" aria-labelledby="vpc-heading">
                <h3 id="vpc-heading" className="curriculum__sectionTitle">
                  The Victorian Pathways Certificate (VPC)
                </h3>
                <p className="curriculum__body">
                  Inclusive and flexible option for completing Secondary Education.<br />
                  For students not yet ready for VCE or VCE VM.<br />
                  Can begin earlier than Year 11 or extend beyond two years.<br />
                  Focuses on practical, classroom-based assessment.<br />
                  Designed collaboratively with students, families, and staff.
                </p>
              </section>
            </Col>
            <Col md={6}>
              <section className="curriculum__col" aria-labelledby="pathways-heading">
                <h3 id="pathways-heading" className="curriculum__sectionTitle">
                  Pathways from the VPC
                </h3>
                <p className="curriculum__body">
                  Transition to VCE or VCE VM.<br />
                  Entry-level VET programs at TAFE.<br />
                  Apprenticeships and traineeships.<br />
                  Direct entry into the workforce.
                </p>
              </section>
            </Col>
          </Row>
        </Container>
      </div>

      {/* FAQ clarifies the certification */}
      <div className="section-stone py-5">
        <Container>
          <div className="card p-4 border-0 shadow-sm bg-white">
            <h3 className="mb-4 text-center" style={{ color: '#4d316c' }}>
              Frequently Asked Questions
            </h3>
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>What is the Victorian Pathways Certificate (VPC)?</Accordion.Header>
                <Accordion.Body style={{ color: '#2b333d' }}>
                  The VPC is a flexible secondary school certificate designed for students who are not ready or able to complete the VCE or VCE VM. It emphasizes practical, hands-on learning.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>Who is eligible for the VPC?</Accordion.Header>
                <Accordion.Body style={{ color: '#2b333d' }}>
                  Students who require a more accessible learning path due to personal, academic, or other challenges. The school collaborates with families to determine suitability.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>What are the future pathways after completing the VPC?</Accordion.Header>
                <Accordion.Body style={{ color: '#2b333d' }}>
                  Graduates can transition to VCE, VCE VM, TAFE, apprenticeships, or directly enter the workforce, depending on their goals and readiness.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="3">
                <Accordion.Header>How long does it take to complete the VPC?</Accordion.Header>
                <Accordion.Body style={{ color: '#2b333d' }}>
                  Typically 2 years (Years 11–12), but it can start earlier or be extended, based on the individual student’s needs.
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </Container>
      </div>
      
    </main>
  );
};

export default Curriculum;
